/**
 * CallOutcomeStrip — strip below the call-decision rationale card.
 * CEO clicks "I took the call" or "I skipped the call", optionally adds a
 * note, and commits via the call-outcome endpoint.
 *
 * Authority:
 *   docs/agents/pitch-deck-review/pages/DEAL_EVALUATOR_PAGE_SPEC_V2.md §15 — TC-DS-CAL-04
 *
 * Token bindings (zero hex):
 *   container        → SECTION_PANEL.adjustFooter (same anatomy as section calibration footer)
 *   ghost buttons    → ACTION_BUTTONS.rerun + ACTION_BUTTONS.hover
 *   inline form      → DS_COLORS.surface.slate + DS_BORDERS.radiusCard
 *   textarea         → ui/textarea.jsx + DS_TYPOGRAPHY.bodyMd
 *   save button      → DS_COLORS.text.brand fill (Primary)
 *   stamp            → DS_COLORS.text.brand on DS_COLORS.surface.warm (took)
 *                      VERDICT_PILL.moderate (skipped)
 *   spacing          → DS_SPACING
 *   loading          → UI_BUTTON_STATE.LOADING
 *
 * Strings: AGENT_CONFIG.deal_evaluator.callOutcomeStrip from task-display-config.js.
 */

import React, { useState } from 'react';
import { Textarea } from '../ui/textarea';
import {
  ACTION_BUTTONS,
  DS_BORDERS,
  DS_COLORS,
  DS_SPACING,
  DS_TYPOGRAPHY,
  SECTION_PANEL,
  UI_BUTTON_STATE,
  VERDICT_PILL,
} from '../../config/ui-invariants';
import { AGENT_CONFIG } from '../../config/task-display-config';
import { recordCallOutcome } from '../../lib/api';

const STRIP_CFG = AGENT_CONFIG.deal_evaluator.callOutcomeStrip;
const FOOTER_CFG = AGENT_CONFIG.deal_evaluator.calibrationFooter;

const ghostButtonStyle = {
  background: ACTION_BUTTONS.rerun.background,
  color: ACTION_BUTTONS.rerun.color,
  border: ACTION_BUTTONS.rerun.border,
  borderRadius: ACTION_BUTTONS.rerun.borderRadius,
  minHeight: ACTION_BUTTONS.rerun.minHeight,
  paddingLeft: '14px',
  paddingRight: '14px',
  cursor: 'pointer',
  ...DS_TYPOGRAPHY.actionBtn,
  transition: 'border-color 150ms cubic-bezier(0.4, 0, 0.2, 1), color 150ms cubic-bezier(0.4, 0, 0.2, 1)',
};

const ghostHoverEnter = (e) => {
  e.currentTarget.style.borderColor = ACTION_BUTTONS.hover.border;
  e.currentTarget.style.color = ACTION_BUTTONS.hover.text;
};
const ghostHoverLeave = (e) => {
  e.currentTarget.style.borderColor = DS_COLORS.border.default;
  e.currentTarget.style.color = ACTION_BUTTONS.rerun.color;
};

const containerStyle = {
  background: SECTION_PANEL.adjustFooter.background,
  borderTop: SECTION_PANEL.adjustFooter.borderTop,
  borderRadius: DS_BORDERS.radiusCard,
  padding: SECTION_PANEL.adjustFooter.padding,
  marginTop: `${DS_SPACING.cardGap}px`,
  display: 'flex',
  flexDirection: 'column',
  gap: `${DS_SPACING.buttonGap}px`,
};

const innerFormStyle = {
  background: DS_COLORS.surface.slate,
  borderRadius: DS_BORDERS.radiusCard,
  border: `${DS_BORDERS.width} solid ${DS_COLORS.border.default}`,
  padding: '14px',
  display: 'flex',
  flexDirection: 'column',
  gap: `${DS_SPACING.labelToContent}px`,
  marginTop: `${DS_SPACING.buttonGap}px`,
};

const noteLabelStyle = {
  ...DS_TYPOGRAPHY.sectionSubLabel,
  color: DS_COLORS.text.secondary,
};

const textareaStyle = {
  ...DS_TYPOGRAPHY.bodyMd,
  fontSize: '14px',
  lineHeight: 1.5,
  color: DS_COLORS.text.body,
  borderColor: DS_COLORS.border.default,
  resize: 'none',
};

const buttonRowStyle = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: `${DS_SPACING.buttonGap}px`,
};

const cancelButtonStyle = {
  background: 'none',
  border: 'none',
  color: DS_COLORS.text.secondary,
  cursor: 'pointer',
  padding: '8px 14px',
  minHeight: `${DS_SPACING.minTouchTarget}px`,
  ...DS_TYPOGRAPHY.actionBtn,
};

const saveButtonBaseStyle = {
  background: DS_COLORS.text.brand,
  color: DS_COLORS.surface.white,
  border: `1.5px solid ${DS_COLORS.border.brand}`,
  borderRadius: DS_BORDERS.radiusButton,
  padding: '0 18px',
  minHeight: `${DS_SPACING.minTouchTarget}px`,
  cursor: 'pointer',
  ...DS_TYPOGRAPHY.actionBtn,
  transition: 'background 200ms cubic-bezier(0.4, 0, 0.2, 1)',
};

const stampBaseStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  padding: '6px 12px',
  borderRadius: DS_BORDERS.radiusPill,
  borderWidth: '1px',
  borderStyle: 'solid',
  ...DS_TYPOGRAPHY.badgeText,
};

const tookCallStampStyle = {
  ...stampBaseStyle,
  background: DS_COLORS.surface.warm,
  color: DS_COLORS.text.brand,
  borderColor: DS_COLORS.border.brand,
};

const skippedCallStampStyle = {
  ...stampBaseStyle,
  background: VERDICT_PILL.moderate.background,
  color: VERDICT_PILL.moderate.text,
  borderColor: VERDICT_PILL.moderate.border,
};

const noteHintStyle = {
  ...DS_TYPOGRAPHY.sectionSubLabel,
  color: DS_COLORS.text.secondary,
};

const undoLinkStyle = {
  ...DS_TYPOGRAPHY.sectionSubLabel,
  color: DS_COLORS.text.secondary,
  background: 'none',
  border: 'none',
  padding: '4px 6px',
  cursor: 'pointer',
  textDecoration: 'underline',
  minHeight: `${DS_SPACING.minTouchTarget}px`,
};

const errorStyle = {
  ...DS_TYPOGRAPHY.sectionSubLabel,
  color: DS_COLORS.error.color,
};

const renderInterpolated = (template, values) =>
  Object.entries(values).reduce(
    (acc, [k, v]) => acc.replace(`{${k}}`, v ?? ''),
    template,
  );

function formatRelativeDate(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  } catch {
    return '';
  }
}

export default function CallOutcomeStrip({
  startupId,
  ceoCallOutcome,
  ceoCallOutcomeAt,
  ceoCallOutcomeNote,
  onOutcomeCommitted,
}) {
  const [pendingOutcome, setPendingOutcome] = useState(null); // 'took_call' | 'skipped_call' | null
  const [note, setNote] = useState('');
  const [savingState, setSavingState] = useState('idle');
  const [errorMessage, setErrorMessage] = useState(null);

  const isSaving = savingState === 'saving';
  const isStamped = Boolean(ceoCallOutcome);
  const isFormOpen = Boolean(pendingOutcome) && !isStamped;

  const handleClickIdle = (outcome) => {
    if (isSaving) return;
    setPendingOutcome(outcome);
    setNote('');
    setErrorMessage(null);
  };

  const handleCancel = () => {
    if (isSaving) return;
    setPendingOutcome(null);
    setNote('');
    setErrorMessage(null);
  };

  const handleSave = async () => {
    if (!pendingOutcome || isSaving) return;
    setSavingState('saving');
    setErrorMessage(null);
    try {
      const response = await recordCallOutcome(startupId, {
        outcome: pendingOutcome,
        ...(note.trim() ? { note: note.trim() } : {}),
      });
      onOutcomeCommitted?.({
        ceo_call_outcome: pendingOutcome,
        ceo_call_outcome_note: note.trim() || null,
        ceo_call_outcome_at: response.recorded_at,
      });
      setPendingOutcome(null);
      setNote('');
      setSavingState('idle');
    } catch (err) {
      setSavingState('error');
      setErrorMessage(STRIP_CFG.toasts.error);
    }
  };

  const handleUndo = () => {
    if (isSaving) return;
    onOutcomeCommitted?.({
      ceo_call_outcome: null,
      ceo_call_outcome_note: null,
      ceo_call_outcome_at: null,
    });
  };

  const loadingButtonStyle = isSaving
    ? {
        opacity: UI_BUTTON_STATE.LOADING.opacity,
        cursor: UI_BUTTON_STATE.LOADING.cursor,
        pointerEvents: UI_BUTTON_STATE.LOADING.pointerEvents,
      }
    : {};

  const saveButtonStyle = pendingOutcome
    ? saveButtonBaseStyle
    : {
        ...saveButtonBaseStyle,
        opacity: UI_BUTTON_STATE.DISABLED.opacity,
        cursor: UI_BUTTON_STATE.DISABLED.cursor,
        pointerEvents: UI_BUTTON_STATE.DISABLED.pointerEvents,
      };

  // STAMPED state — CEO has already recorded an outcome
  if (isStamped) {
    const isTook = ceoCallOutcome === 'took_call';
    const stampStyle = isTook ? tookCallStampStyle : skippedCallStampStyle;
    const template = isTook
      ? STRIP_CFG.tookCallStampTemplate
      : STRIP_CFG.skippedCallStampTemplate;
    const stampText = renderInterpolated(template, {
      date: formatRelativeDate(ceoCallOutcomeAt),
    });
    return (
      <div style={containerStyle} data-testid="call-outcome-strip-stamped">
        <div style={{ display: 'flex', alignItems: 'center', gap: `${DS_SPACING.buttonGap}px`, flexWrap: 'wrap' }}>
          <span style={stampStyle} data-testid="call-outcome-stamp">
            <span aria-hidden="true">{isTook ? '✓' : '→'}</span>
            <span>{stampText}</span>
          </span>
          <button
            type="button"
            onClick={handleUndo}
            disabled={isSaving}
            style={undoLinkStyle}
            data-testid="call-outcome-undo"
          >
            {STRIP_CFG.undoLabel}
          </button>
        </div>
        {ceoCallOutcomeNote && (
          <div style={noteHintStyle} data-testid="call-outcome-note">
            {renderInterpolated(STRIP_CFG.noteHintTemplate, { note: ceoCallOutcomeNote })}
          </div>
        )}
      </div>
    );
  }

  // IDLE or FORM_OPEN state — both render the two buttons; FORM_OPEN appends the inline form
  return (
    <div style={containerStyle} data-testid="call-outcome-strip">
      <div style={{ display: 'flex', gap: `${DS_SPACING.buttonGap}px`, flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={() => handleClickIdle('took_call')}
          onMouseEnter={ghostHoverEnter}
          onMouseLeave={ghostHoverLeave}
          disabled={isSaving}
          style={{
            ...ghostButtonStyle,
            ...loadingButtonStyle,
            ...(pendingOutcome === 'took_call'
              ? { borderColor: ACTION_BUTTONS.hover.border, color: ACTION_BUTTONS.hover.text }
              : {}),
          }}
          aria-pressed={pendingOutcome === 'took_call'}
          data-testid="call-outcome-took-button"
        >
          {STRIP_CFG.tookCallButton.label}
        </button>
        <button
          type="button"
          onClick={() => handleClickIdle('skipped_call')}
          onMouseEnter={ghostHoverEnter}
          onMouseLeave={ghostHoverLeave}
          disabled={isSaving}
          style={{
            ...ghostButtonStyle,
            ...loadingButtonStyle,
            ...(pendingOutcome === 'skipped_call'
              ? { borderColor: ACTION_BUTTONS.hover.border, color: ACTION_BUTTONS.hover.text }
              : {}),
          }}
          aria-pressed={pendingOutcome === 'skipped_call'}
          data-testid="call-outcome-skipped-button"
        >
          {STRIP_CFG.skippedCallButton.label}
        </button>
      </div>

      {isFormOpen && (
        <div style={innerFormStyle} data-testid="call-outcome-form">
          <label style={noteLabelStyle} htmlFor="call-outcome-note-input">
            {STRIP_CFG.noteLabel}
          </label>
          <Textarea
            id="call-outcome-note-input"
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={STRIP_CFG.noteMaxChars}
            placeholder={STRIP_CFG.notePlaceholder}
            style={textareaStyle}
            data-testid="call-outcome-note-input"
          />
          <div style={buttonRowStyle}>
            <button
              type="button"
              onClick={handleCancel}
              disabled={isSaving}
              style={cancelButtonStyle}
              data-testid="call-outcome-cancel"
            >
              {STRIP_CFG.cancelLabel}
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={!pendingOutcome || isSaving}
              style={{ ...saveButtonStyle, ...loadingButtonStyle }}
              data-testid="call-outcome-save"
            >
              {isSaving ? STRIP_CFG.loadingLabel : STRIP_CFG.confirmLabel}
            </button>
          </div>
          {errorMessage && (
            <div style={errorStyle} role="alert" data-testid="call-outcome-error">
              {errorMessage}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
